import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { Bell, Cable, FlaskConical, Images, LogOut, Search, Settings, Tag, UserRoundPlus, Users } from 'lucide-react';

import db from '../../lib/db';
import { cn } from '../../lib/utils';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import useLeaveWorkspace from '../../hooks/useLeaveWorkspace';
import useModalSections from '../../hooks/useModalSections';
import NotificationsSettings, { notificationPreferenceLabel } from '../settings/workspace/NotificationsSettings';
import ConnectionsSettings from '../settings/workspace/ConnectionsSettings';
import BetaFeaturesPanel from '../shared/BetaFeaturesPanel';
import ChatUrlPanel from '../shared/ChatUrlPanel';
import Flexicon from '../shared/Flexicon';
import GroupProfilePanel from '../shared/GroupProfilePanel';
import MediaGalleryPanel from '../shared/MediaGalleryPanel';
import MembersPanel, { ACTIVE_MEMBER_ROLES } from '../shared/MembersPanel';
import ProfileTag from '../shared/ProfileTag';
import SettingsBackButton from '../shared/SettingsBackButton';
import { SettingsGroup, SettingsQuickAction, SettingsRow } from '../shared/SettingsList';
import WorkspaceSettingsPanel from '../shared/WorkspaceSettingsPanel';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_TITLE_CLASS } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalTopBar } from './ModalTopBar';

export const WORKSPACE_OPTIONS_SECTION = {
  ROOT: 'root',
  GROUP_PROFILE: 'group_profile',
  CHAT_SETTINGS: 'chat_settings',
  CHAT_URL: 'chat_url',
  BETA_FEATURES: 'beta_features',
  NOTIFICATIONS: 'notifications',
  MEMBERS: 'members',
  MEDIA: 'media',
  CONNECTIONS: 'connections',
};
const SECTION = WORKSPACE_OPTIONS_SECTION;
const SECTION_TITLE = {
  [SECTION.GROUP_PROFILE]: 'Name Tag',
  [SECTION.CHAT_SETTINGS]: 'Chat Settings',
  [SECTION.CHAT_URL]: 'Chat URL',
  [SECTION.BETA_FEATURES]: 'Beta Features',
  [SECTION.NOTIFICATIONS]: 'Notifications',
  [SECTION.MEMBERS]: 'Members',
  [SECTION.MEDIA]: 'Media',
  [SECTION.CONNECTIONS]: 'Connections',
};
const MODAL_SECTION_RESET_DELAY_MS = 200;

export const WORKSPACE_OPTIONS_MEMBERS_PRESENTATION = {
  SECTION: 'section',
  MODAL: 'modal',
};

const WorkspaceOptionsModal = ({
  open,
  onClose,
  onOpenInvitePeople,
  onOpenMembers,
  onOpenSearch,
  defaultSection = SECTION.ROOT,
  membersPresentation = WORKSPACE_OPTIONS_MEMBERS_PRESENTATION.SECTION,
}) => {
  const { workspace, workspaceId, profile } = useRouteWorkspace();
  const isAdmin = profile?.role === 'admin';
  const { leave, isLeaving } = useLeaveWorkspace();
  const { section, isNested, goToSection, goBack } = useModalSections({
    open,
    initialSection: SECTION.ROOT,
    resetDelayMs: MODAL_SECTION_RESET_DELAY_MS,
  });
  const workspaceTitle = workspace?.title || 'Untitled';
  const sectionTitle = SECTION_TITLE[section] || null;
  const appliedDefaultSectionRef = useRef(null);
  const modalContentRef = useRef(null);

  useEffect(() => {
    if (!modalContentRef.current) return;
    modalContentRef.current.scrollTop = 0;
  }, [section]);

  const memberCount = useLiveQuery(
    () => {
      if (!workspaceId) return 0;
      return db.profiles
        .where('workspace_id')
        .equals(workspaceId)
        .filter((memberProfile) => ACTIVE_MEMBER_ROLES.includes(memberProfile.role))
        .count();
    },
    [workspaceId],
    0,
  );
  const membersLabel = `${memberCount} ${memberCount === 1 ? 'Member' : 'Members'}`;

  const notificationsValue = notificationPreferenceLabel(profile?.notification_preference);
  const nameTagValue = profile?.tag ? (
    <ProfileTag tag={profile.tag} hex={profile.tag_color} />
  ) : (
    <span className="font-mono text-[11px] font-semibold text-gray-400 dark:text-neutral-500">NONE</span>
  );

  const handleOpenGroupProfile = useCallback(() => goToSection(SECTION.GROUP_PROFILE), [goToSection]);
  const handleOpenNotifications = useCallback(() => goToSection(SECTION.NOTIFICATIONS), [goToSection]);
  const handleOpenMedia = useCallback(() => goToSection(SECTION.MEDIA), [goToSection]);
  const handleOpenChatSettings = useCallback(() => goToSection(SECTION.CHAT_SETTINGS), [goToSection]);
  const handleOpenBetaFeatures = useCallback(() => goToSection(SECTION.BETA_FEATURES), [goToSection]);
  const handleOpenMembers = useCallback(
    function openMembers() {
      if (membersPresentation === WORKSPACE_OPTIONS_MEMBERS_PRESENTATION.SECTION) {
        goToSection(SECTION.MEMBERS);
        return;
      }
      onOpenMembers?.();
    },
    [goToSection, membersPresentation, onOpenMembers],
  );
  const handleOpenConnections = useCallback(() => goToSection(SECTION.CONNECTIONS), [goToSection]);

  const isSectionAvailable = useCallback(
    (targetSection) => {
      switch (targetSection) {
        case SECTION.ROOT:
        case SECTION.GROUP_PROFILE:
        case SECTION.NOTIFICATIONS:
        case SECTION.MEDIA:
        case SECTION.CONNECTIONS:
          return true;
        case SECTION.MEMBERS:
          return membersPresentation === WORKSPACE_OPTIONS_MEMBERS_PRESENTATION.SECTION;
        case SECTION.CHAT_SETTINGS:
        case SECTION.CHAT_URL:
        case SECTION.BETA_FEATURES:
          return isAdmin;
        default:
          return false;
      }
    },
    [isAdmin, membersPresentation],
  );

  useLayoutEffect(() => {
    if (!open) {
      appliedDefaultSectionRef.current = null;
      return;
    }

    const resolvedDefaultSection = isSectionAvailable(defaultSection) ? defaultSection : SECTION.ROOT;
    if (appliedDefaultSectionRef.current === resolvedDefaultSection) return;
    if (appliedDefaultSectionRef.current !== null && section !== appliedDefaultSectionRef.current) {
      appliedDefaultSectionRef.current = resolvedDefaultSection;
      return;
    }

    appliedDefaultSectionRef.current = resolvedDefaultSection;
    goToSection(resolvedDefaultSection);
  }, [defaultSection, goToSection, isSectionAvailable, open, section]);

  return (
    <Modal
      onClose={onClose}
      className="rounded-2xl"
      closePlacement="top-left"
      showClose={{ desktop: !isNested, mobile: !isNested }}
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      {isNested && <SettingsBackButton onClick={goBack} />}
      {/* Root: the body's top inset moves inside the scroller (!pt-0 + pt-5) so
          scrolled content runs flush to the modal's top edge instead of clipping
          against a padding band. */}
      <ModalBody className={cn('h-full', section === SECTION.ROOT && '!pt-0')}>
        {isNested && (
          <ModalTopBar className="!pb-0">
            <span className={SETTINGS_MODAL_TITLE_CLASS}>{sectionTitle}</span>
          </ModalTopBar>
        )}
        <ModalContent
          ref={modalContentRef}
          className={cn('min-h-0 md:!px-5', section === SECTION.ROOT && 'flex items-start justify-center pt-5')}
        >
          {section === SECTION.ROOT && (
            <div className="flex w-full flex-col items-center pt-3 pb-2">
              <span className="inline-flex aspect-square size-30 overflow-hidden rounded-full">
                <Flexicon resource={workspace} size="size-30" rounded="rounded-full" className="size-30" />
              </span>
              <h2 className="mt-3 max-w-full truncate text-center text-xl leading-6 font-semibold text-black dark:text-neutral-100">
                {workspaceTitle}
              </h2>
              <div className="mt-6.5 flex items-center gap-3">
                <SettingsQuickAction icon={Search} label="Search" onClick={onOpenSearch} />
                <SettingsQuickAction icon={Images} label="Media" onClick={handleOpenMedia} />
                <SettingsQuickAction icon={UserRoundPlus} label="Add" onClick={onOpenInvitePeople} />
              </div>

              <div className="mt-7 flex w-full flex-col gap-5">
                <SettingsGroup>
                  <SettingsRow label="Name Tag" icon={Tag} value={nameTagValue} onClick={handleOpenGroupProfile} />
                  <SettingsRow label="Notifications" icon={Bell} value={notificationsValue} onClick={handleOpenNotifications} />
                </SettingsGroup>

                <SettingsGroup>
                  <SettingsRow label={membersLabel} icon={Users} chevron onClick={handleOpenMembers} />
                  <SettingsRow label="Connections" icon={Cable} chevron onClick={handleOpenConnections} />
                </SettingsGroup>

                {isAdmin && (
                  <SettingsGroup>
                    <SettingsRow label="Chat Settings" icon={Settings} chevron onClick={handleOpenChatSettings} />
                    <SettingsRow label="Beta Features" icon={FlaskConical} chevron onClick={handleOpenBetaFeatures} />
                  </SettingsGroup>
                )}

                <SettingsGroup>
                  <SettingsRow label="Leave" icon={LogOut} destructive onClick={leave} disabled={isLeaving} />
                </SettingsGroup>
              </div>
            </div>
          )}

          {section === SECTION.GROUP_PROFILE && <GroupProfilePanel onSaved={goBack} />}
          {section === SECTION.CHAT_SETTINGS && <WorkspaceSettingsPanel onSaved={goBack} />}
          {section === SECTION.CHAT_URL && <ChatUrlPanel onSaved={goBack} />}
          {section === SECTION.BETA_FEATURES && <BetaFeaturesPanel />}
          {section === SECTION.NOTIFICATIONS && <NotificationsSettings />}
          {section === SECTION.MEMBERS && <MembersPanel />}
          {section === SECTION.MEDIA && <MediaGalleryPanel />}
          {section === SECTION.CONNECTIONS && <ConnectionsSettings workspaceId={workspaceId} />}
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

export default WorkspaceOptionsModal;
